import { Link } from 'react-router-dom';

interface HeroSlideProps {
  id: string;
  title: string;
  tagline: string;
  summary: string;
  backdrop: string;
  poster: string;
  typeLabelText: string;
  genre: string;
  region: string;
  year: number;
  episodes: string;
  rating: number;
  active: boolean;
}

export default function HeroSlide({
  id,
  title,
  tagline,
  summary,
  backdrop,
  poster,
  typeLabelText,
  genre,
  region,
  year,
  episodes,
  rating,
  active,
}: HeroSlideProps) {
  return (
    <div
      className={`absolute inset-0 transition-opacity duration-700 ${active ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'}`}
    >
      <div className="absolute inset-0 overflow-hidden">
        <img
          src={backdrop}
          alt={`${title} 剧照`}
          title={`${title} ${genre} 高清在线观看`}
          className={`w-full h-full object-cover object-center ${active ? 'animate-slow-zoom' : ''}`}
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/75 to-primary-950/25" />
      <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-transparent to-primary-950/40" />

      <div className="relative h-full w-full px-4 md:px-8 flex items-center">
        <div className="flex items-center gap-6 md:gap-9 w-full max-w-6xl">
          <div className="hidden sm:block w-[150px] md:w-[200px] shrink-0">
            <div className="w-full aspect-[2/3] rounded-lg overflow-hidden border border-background-50/20 bg-primary-900">
              <img
                src={poster}
                alt={`${title} 海报`}
                title={`${title} 海报 ${year}`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className={`flex-1 ${active ? 'animate-fade-up' : ''}`}>
            <div className="flex flex-wrap items-center gap-2 text-[11px]">
              <span className="px-2 py-0.5 rounded-md bg-accent-500 text-primary-950 font-semibold whitespace-nowrap">
                独家推荐
              </span>
              <span className="px-2 py-0.5 rounded-md bg-background-50/15 text-background-50 backdrop-blur whitespace-nowrap">
                {typeLabelText}
              </span>
              <span className="px-2 py-0.5 rounded-md bg-background-50/15 text-background-50 backdrop-blur whitespace-nowrap">
                {genre}
              </span>
              <span className="flex items-center gap-1 text-accent-300 font-semibold whitespace-nowrap">
                <i className="ri-star-fill text-[11px]" />
                {rating.toFixed(1)}
              </span>
            </div>

            <h1 className="mt-3 md:mt-4 text-2xl md:text-5xl font-black text-background-50 tracking-tight leading-tight">
              {title}
            </h1>
            <p className="mt-2 md:mt-3 text-sm md:text-lg text-accent-300 font-medium">{tagline}</p>
            <p className="mt-2 md:mt-3 max-w-xl text-xs md:text-sm leading-6 md:leading-7 text-background-100/80 line-clamp-3-safe">
              {summary}
            </p>

            <div className="mt-3 md:mt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] md:text-xs text-background-200/70">
              <span>{year}</span>
              <span className="w-0.5 h-0.5 rounded-full bg-background-200/50" />
              <span>{region}</span>
              <span className="w-0.5 h-0.5 rounded-full bg-background-200/50" />
              <span>{episodes}</span>
            </div>

            <div className="mt-5 md:mt-7 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${id}`}
                className="h-10 md:h-11 px-5 md:px-6 rounded-md bg-accent-500 hover:bg-accent-600 text-primary-950 text-sm font-semibold flex items-center gap-2 whitespace-nowrap transition-colors"
              >
                <i className="ri-play-fill text-base" />
                立即观看
              </Link>
              <Link
                to={`/detail/${id}`}
                className="h-10 md:h-11 px-5 md:px-6 rounded-md border border-background-50/30 text-background-50 text-sm font-medium flex items-center gap-2 whitespace-nowrap hover:bg-background-50/10 transition-colors"
              >
                <i className="ri-information-line text-base" />
                影视详情
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}